import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, ProgressBar, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileUpload, 
  faDownload, 
  faTrashAlt,
  faFilePdf,
  faFileImage,
  faFile
} from '@fortawesome/free-solid-svg-icons';
import { uploadSongPart, getSongPartUrl, deleteSongPart } from '../../services/songService';

/**
 * Modal component for managing song part files (upload, view, download, delete)
 */
const SongPartManager = ({ show, onHide, song, onPartUpdated }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset state when modal opens with a new song
  useEffect(() => {
    if (show) {
      setSelectedFile(null);
      setUploadProgress(0);
      setError(null);
      setSuccess(null);
    }
  }, [show, song?.id]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Velikost datoteke je prevelika. Največja dovoljena je 10MB.');
        return;
      }
      
      // Check file type
      const fileExt = file.name.split('.').pop().toLowerCase();
      const allowedTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'tif', 'tiff'];
      if (!allowedTypes.includes(fileExt)) {
        setError(`Vrsta datoteke ni dovoljena. Dovoljene so: ${allowedTypes.join(', ')}`);
        return;
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  // Format file size to human-readable format
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
  };

  // Get file icon based on file type
  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') {
      return faFilePdf;
    } else if (['jpg', 'jpeg', 'png', 'gif', 'tif', 'tiff'].includes(fileType)) {
      return faFileImage;
    } else {
      return faFile;
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Izberite datoteko za nalaganje.');
      return;
    }
    
    setLoading(true);
    setUploadProgress(0);
    setError(null);
    setSuccess(null);
    
    try {
      const updatedSong = await uploadSongPart(
        song.id,
        selectedFile,
        (progress) => setUploadProgress(progress)
      );
      
      setSuccess('Datoteka uspešno naložena.');
      setSelectedFile(null);
      
      // Call the callback to update the parent component
      if (onPartUpdated) {
        onPartUpdated(updatedSong);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Napaka pri nalaganju datoteke.');
    } finally {
      setLoading(false);
    }
  };

  // Handle file download
  const handleDownload = () => {
    if (!song) return;
    
    // Create an anchor element and trigger download
    const downloadUrl = getSongPartUrl(song.id);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = song.part_file_name || `part-${song.id}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Handle file deletion
  const handleDelete = async () => {
    if (!song) return;
    
    if (!window.confirm('Ali ste prepričani, da želite izbrisati to datoteko?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await deleteSongPart(song.id);
      
      setSuccess('Datoteka uspešno izbrisana.');
      
      // Call the callback to update the parent component
      if (onPartUpdated) {
        const updatedSong = { ...song, has_part_file: false, part_file_name: null };
        onPartUpdated(updatedSong);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Napaka pri brisanju datoteke.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {song?.has_part_file 
            ? 'Upravjaj notni zapis' 
            : 'Naloži notni zapis'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        {song?.has_part_file ? (
          <div className="part-file-info mb-4 p-3 border rounded">
            <h5>
              <FontAwesomeIcon 
                icon={getFileIcon(song.part_file_type)} 
                className={song.part_file_type === 'pdf' ? 'text-danger' : 'text-primary'} 
                style={{ marginRight: '10px' }} 
              />
              {song.part_file_name}
            </h5>
            <p className="text-muted mb-2">
              Vrsta: {song.part_file_type?.toUpperCase() || 'Neznano'}, 
              Velikost: {formatFileSize(song.part_file_size || 0)}
            </p>
            <p className="text-muted mb-3">
              Naloženo: {song.part_file_uploaded_at ? new Date(song.part_file_uploaded_at).toLocaleString() : 'Neznano'}
            </p>
            
            <div className="d-flex">
              <Button 
                variant="primary" 
                className="me-2" 
                onClick={handleDownload}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Prenesi
              </Button>
              <Button 
                variant="danger" 
                onClick={handleDelete}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                Izbriši
              </Button>
            </div>
          </div>
        ) : (
          <div className="upload-form">
            <Form.Group className="mb-3">
              <Form.Label>Izberite datoteko</Form.Label>
              <Form.Control 
                type="file" 
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.tif,.tiff"
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Dovoljene so datoteke PDF, JPG, PNG (največ 10MB)
              </Form.Text>
            </Form.Group>
            
            {selectedFile && (
              <div className="selected-file-info mb-3 p-2 border rounded">
                <p className="mb-1">
                  <strong>Izbrana datoteka:</strong> {selectedFile.name}
                </p>
                <p className="mb-0 text-muted">
                  Velikost: {formatFileSize(selectedFile.size)}, 
                  Vrsta: {selectedFile.type || 'Neznano'}
                </p>
              </div>
            )}
            
            {loading && uploadProgress > 0 && (
              <div className="mt-3 mb-3">
                <ProgressBar 
                  now={uploadProgress} 
                  label={`${uploadProgress}%`} 
                  animated
                />
              </div>
            )}
            
            <div className="mt-3">
              <Button 
                variant="primary" 
                onClick={handleUpload}
                disabled={!selectedFile || loading}
              >
                {loading ? (
                  <>
                    <Spinner 
                      as="span" 
                      animation="border" 
                      size="sm" 
                      role="status" 
                      aria-hidden="true" 
                      className="me-2"
                    />
                    Nalaganje...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                    Naloži datoteko
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SongPartManager;